// src/pages/dashboard/branch/agent/AgentConfigurationPage.tsx
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FiInfo,
  FiMessageCircle,
  FiMic,
  FiPower,
  FiRefreshCw,
  FiSave,
  FiSettings,
  FiShield,
  FiSmile,
  FiStar,
  FiTarget,
  FiUser,
} from "react-icons/fi";
import { GrConfigure } from "react-icons/gr";
import { useParams } from "react-router-dom";

import Autocomplete from "../../../../components/ui/Autocomplete";
import Badge from "../../../../components/ui/Badge";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/Card";
import Skeleton from "../../../../components/ui/Skeleton";
import Toggle from "../../../../components/ui/Toggle";

import {
  AgentConfigurationInput,
  HELPING_STRATEGY_OPTIONS,
  IDENTITY_OPTIONS,
  LANGUAGE_OPTIONS,
  RESPONSE_LENGTH_OPTIONS,
  TONE_OPTIONS,
} from "../../../../types/agent";

import {
  getAgentConfiguration,
  upsertAgentConfiguration,
} from "../../../../services/agentService";
import InputField from "../../../../components/ui/InputField";
import { publishBranch } from "../../../../store/slices/branchSlice";
import { useAppSelector } from "../../../../store/hooks";

// Predefined profiles based on the Python configuration
const PREDEFINED_PROFILES = [
  {
    id: "lebanese_casual",
    nameKey: "agent.profiles.lebaneseCasual.name",
    descriptionKey: "agent.profiles.lebaneseCasual.description",
    icon: "🇱🇧",
    config: {
      tone: "friendly",
      language: "lebanese_arabish",
      identity: "dining_assistant",
      helping_strategy: "conversational",
      include_food_safety: true,
      mention_featured_items: true,
      use_humor: true,
      response_length: "medium",
    },
  },
  {
    id: "professional_english",
    nameKey: "agent.profiles.professionalEnglish.name",
    descriptionKey: "agent.profiles.professionalEnglish.description",
    icon: "💼",
    config: {
      tone: "professional",
      language: "english",
      identity: "dining_assistant",
      helping_strategy: "consultative",
      include_food_safety: true,
      mention_featured_items: true,
      use_humor: false,
      response_length: "medium",
    },
  },
  {
    id: "quick_service",
    nameKey: "agent.profiles.quickService.name",
    descriptionKey: "agent.profiles.quickService.description",
    icon: "⚡",
    config: {
      tone: "energetic",
      language: "english",
      identity: "friendly_waiter",
      helping_strategy: "quick_service",
      include_food_safety: true,
      mention_featured_items: true,
      use_humor: true,
      response_length: "short",
    },
  },
  {
    id: "chef_expert",
    nameKey: "agent.profiles.chefExpert.name",
    descriptionKey: "agent.profiles.chefExpert.description",
    icon: "👨‍🍳",
    config: {
      tone: "warm",
      language: "english",
      identity: "chef_consultant",
      helping_strategy: "educational",
      include_food_safety: true,
      mention_featured_items: true,
      use_humor: false,
      response_length: "long",
    },
  },
];

const AgentConfigurationPage: React.FC = () => {
  const { t } = useTranslation();
  const { branchId } = useParams<{ branchId: string }>();
  const organization = useAppSelector((state) => state.organization.organization);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [configuration, setConfiguration] = useState<AgentConfigurationInput>({
    is_active: false,
    agent_name: "",
    tone: "friendly",
    language: "lebanese_arabish",
    identity: "dining_assistant",
    helping_strategy: "conversational",
    include_food_safety: true,
    mention_featured_items: true,
    use_humor: true,
    response_length: "medium",
    ordering: false,
  });

  const [originalConfiguration, setOriginalConfiguration] =
    useState<AgentConfigurationInput>(configuration);

  useEffect(() => {
    const fetchData = async () => {
      if (!branchId) return;

      setIsLoading(true);
      try {
        // Fetch branch info and agent configuration
        const [configData] = await Promise.all([
          getAgentConfiguration(branchId),
        ]);

        if (configData.data) {
          const config: AgentConfigurationInput = {
            is_active: configData.data.is_active ?? false,
            agent_name: configData.data.agent_name ?? '',
            tone: configData.data.tone ?? 'friendly',
            language: configData.data.language ?? 'lebanese_arabish',
            identity: configData.data.identity ?? 'dining_assistant',
            helping_strategy: configData.data.helping_strategy ?? 'conversational',
            include_food_safety: configData.data.include_food_safety ?? false,
            mention_featured_items: configData.data.mention_featured_items ?? false,
            use_humor: configData.data.use_humor ?? false,
            response_length: (configData.data.response_length as any) ?? 'medium',
            ordering: configData.data.ordering ?? false,
          };
          setConfiguration(config);
          setOriginalConfiguration(config);
        }
      } catch (err) {
        console.error("Error fetching agent configuration:", err);
        setError(t("agent.errors.fetchFailed"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [branchId, t]);

  // Check for changes
  useEffect(() => {
    if (originalConfiguration) {
      const hasChanges =
        JSON.stringify(configuration) !== JSON.stringify(originalConfiguration);
      setHasChanges(hasChanges);
    }
  }, [configuration, originalConfiguration]);

  const handleConfigChange = (
    key: keyof AgentConfigurationInput,
    value: any
  ) => {
    setConfiguration((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyProfile = (profile: (typeof PREDEFINED_PROFILES)[0]) => {
    setConfiguration((prev) => ({
      ...prev,
      ...profile.config,
      response_length: profile.config.response_length as
        | "short"
        | "medium"
        | "long",
    }));
  };

  const handleSave = async () => {
    if (!branchId || !organization?.id) return;
    publishBranch({ branchId, organizationId: organization.id });
    setIsSaving(true);
    setError(null);

    try {
      const { is_active, ...configToSave } = configuration;
      const { data, error } = await upsertAgentConfiguration(branchId, {
        is_active,
        ...configToSave,
      });

      if (error) throw error;

      setOriginalConfiguration(configuration);
      setHasChanges(false);
    } catch (err) {
      console.error("Error saving agent configuration:", err);
      setError(t("agent.errors.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (originalConfiguration) {
      setConfiguration(originalConfiguration);
      setHasChanges(false);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-6">
          <Skeleton height="h-8" width="w-64" className="mb-2" />
          <Skeleton height="h-4" width="w-96" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-6">
                <Skeleton height="h-6" width="w-48" className="mb-4" />
                <div className="space-y-4">
                  <Skeleton height="h-12" width="w-full" />
                  <Skeleton height="h-12" width="w-full" />
                </div>
              </div>
            ))}
          </div>
          <div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6">
              <Skeleton height="h-6" width="w-32" className="mb-4" />
              <Skeleton height="h-20" width="w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-3">
            {/* <FaRobot className="text-primary" /> */}
            {t("agent.title")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("agent.subtitle")}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {hasChanges && (
            <Badge variant="warning" className="animate-pulse">
              {t("agent.unsavedChanges")}
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges || isSaving}
            leftIcon={<FiRefreshCw />}
          >
            {t("common.reset")}
          </Button>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
            disabled={!hasChanges}
            leftIcon={<FiSave />}
          >
            {t("common.save")}
          </Button>
        </div>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-6"
        >
          {error}
        </motion.div>
      )}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 lg:grid-cols-3 gap-8"
      >
        {/* Configuration Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Agent Status */}
          <motion.div variants={itemVariants}>
            <Card className="sm:p-4">
              <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
                <div className="w-full flex gap-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${configuration.is_active
                      ? "bg-green-100 dark:bg-green-900/30"
                      : "bg-gray-100 dark:bg-gray-700"
                      }`}
                  >
                    <FiPower
                      className={`w-5 h-5 ${configuration.is_active
                        ? "text-green-600 dark:text-green-400"
                        : "text-gray-500"
                        }`}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t("agent.agentStatus")}
                      </h3>
                      <Toggle
                        enabled={configuration.is_active}
                        onChange={(enabled) =>
                          handleConfigChange("is_active", enabled)
                        }
                        activeColor="bg-green-500"
                      />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {configuration.is_active
                        ? t("agent.agentActive")
                        : t("agent.agentInactive")}
                    </p>
                  </div>
                </div>
                {/* <div className="flex items-center justify-between gap-3">
									<Badge
										variant={configuration.is_active ? 'success' : 'gray'}
										className="flex items-center gap-1"
									>
										<div className={`w-2 h-2 rounded-full ${configuration.is_active ? 'bg-green-500' : 'bg-gray-400'
											} animate-pulse`} />
										{configuration.is_active ? t('agent.active') : t('agent.inactive')}
									</Badge>
									<Toggle
										enabled={configuration.is_active}
										onChange={(enabled) => handleConfigChange('is_active', enabled)}
										activeColor="bg-green-500"
									/>
								</div> */}
              </div>
            </Card>
          </motion.div>

          {/* Predefined Profiles */}
          <motion.div variants={itemVariants}>
            <Card
              className={`sm:p-4 transition-all duration-300 ${!configuration.is_active ? "opacity-50 pointer-events-none" : ""
                }`}
            >
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                  <FiUser className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t("agent.predefinedProfiles")}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("agent.predefinedProfilesDesc")}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PREDEFINED_PROFILES.map((profile) => (
                  <motion.button
                    key={profile.id}
                    onClick={() => applyProfile(profile)}
                    disabled={!configuration.is_active}
                    whileHover={configuration.is_active ? { scale: 1.02 } : {}}
                    whileTap={configuration.is_active ? { scale: 0.98 } : {}}
                    className="p-4 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 transition-all text-start cursor-pointer group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-2xl">{profile.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-primary">
                          {t(profile.nameKey)}
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {t(profile.descriptionKey)}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          <Badge variant="gray" size="sm">
                            {t(
                              TONE_OPTIONS.find(
                                (opt) => opt.value === profile.config.tone
                              )?.labelKey || profile.config.tone
                            )}
                          </Badge>
                          <Badge variant="gray" size="sm">
                            {t(
                              LANGUAGE_OPTIONS.find(
                                (opt) => opt.value === profile.config.language
                              )?.labelKey || profile.config.language
                            )}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </Card>
          </motion.div>

          {/* Basic Configuration */}
          <motion.div variants={itemVariants}>
            <Card
              className={`sm:p-4 transition-all duration-300 ${!configuration.is_active ? "opacity-50 pointer-events-none" : ""
                }`}
            >
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                  <GrConfigure className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t("agent.basicConfiguration")}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("agent.basicConfigurationDesc")}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <InputField
                  label={t("agent.agentName")}
                  value={configuration.agent_name || ""}
                  onChange={(e) => handleConfigChange("agent_name", e.target.value)}
                  placeholder={t("agent.agentNamePlaceholder")}
                  disabled={!configuration.is_active}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Autocomplete
                  label={t("agent.tone")}
                  value={configuration.tone}
                  onChange={(value) => handleConfigChange("tone", value)}
                  options={TONE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: t(option.labelKey),
                    description: t(option.descriptionKey),
                  }))}
                  placeholder={t("agent.selectTone")}
                  allowCustom={false}
                  disabled={!configuration.is_active}
                />

                <Autocomplete
                  label={t("agent.language")}
                  value={configuration.language}
                  onChange={(value) => handleConfigChange("language", value)}
                  options={LANGUAGE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: t(option.labelKey),
                    description: t(option.descriptionKey),
                  }))}
                  placeholder={t("agent.selectLanguage")}
                  allowCustom={false}
                  disabled={!configuration.is_active}
                />

                <Autocomplete
                  label={t("agent.identity")}
                  value={configuration.identity}
                  onChange={(value) => handleConfigChange("identity", value)}
                  options={IDENTITY_OPTIONS.map((option) => ({
                    value: option.value,
                    label: t(option.labelKey),
                    description: t(option.descriptionKey),
                  }))}
                  placeholder={t("agent.selectIdentity")}
                  allowCustom={false}
                  disabled={!configuration.is_active}
                />

                <Autocomplete
                  label={t("agent.helpingStrategy")}
                  value={configuration.helping_strategy}
                  onChange={(value) =>
                    handleConfigChange("helping_strategy", value)
                  }
                  options={HELPING_STRATEGY_OPTIONS.map((option) => ({
                    value: option.value,
                    label: t(option.labelKey),
                    description: t(option.descriptionKey),
                  }))}
                  placeholder={t("agent.selectStrategy")}
                  allowCustom={false}
                  disabled={!configuration.is_active}
                />
              </div>
            </Card>
          </motion.div>

          {/* Response Configuration */}
          <motion.div variants={itemVariants}>
            <Card
              className={`sm:p-4 transition-all duration-300 ${!configuration.is_active ? "opacity-50 pointer-events-none" : ""
                }`}
            >
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <FiMic className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t("agent.responseConfiguration")}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("agent.responseConfigurationDesc")}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t("agent.responseLength")}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {RESPONSE_LENGTH_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() =>
                          handleConfigChange("response_length", option.value)
                        }
                        disabled={!configuration.is_active}
                        className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${configuration.response_length === option.value
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                          } ${!configuration.is_active ? "cursor-not-allowed" : ""
                          }`}
                      >
                        <div className="text-center">
                          <div className="font-medium">
                            {t(option.labelKey)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {t(option.descriptionKey)}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Behavior Settings */}
          <motion.div variants={itemVariants}>
            <Card
              className={`sm:p-4 transition-all duration-300 ${!configuration.is_active ? "opacity-50 pointer-events-none" : ""
                }`}
            >
              <div className="flex gap-3 mb-6">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                  <FiSettings className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t("agent.behaviorSettings")}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("agent.behaviorSettingsDesc")}
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex gap-3">
                    <FiShield className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {t("agent.includeFoodSafety")}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("agent.includeFoodSafetyDesc")}
                      </div>
                    </div>
                  </div>
                  <Toggle
                    enabled={configuration.include_food_safety}
                    onChange={(enabled) =>
                      handleConfigChange("include_food_safety", enabled)
                    }
                    disabled={!configuration.is_active}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex gap-3">
                    <FiStar className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {t("agent.mentionFeaturedItems")}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("agent.mentionFeaturedItemsDesc")}
                      </div>
                    </div>
                  </div>
                  <Toggle
                    enabled={configuration.mention_featured_items}
                    onChange={(enabled) =>
                      handleConfigChange("mention_featured_items", enabled)
                    }
                    disabled={!configuration.is_active}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex gap-3">
                    <FiSmile className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {t("agent.useHumor")}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("agent.useHumorDesc")}
                      </div>
                    </div>
                  </div>
                  <Toggle
                    enabled={configuration.use_humor}
                    onChange={(enabled) =>
                      handleConfigChange("use_humor", enabled)
                    }
                    disabled={!configuration.is_active}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex gap-3">
                    <FiSmile className="w-5 h-5 text-gray-500 mt-1" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {t("agent.enableOrdering")}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {t("agent.enableOrderingDesc")}
                      </div>
                    </div>
                  </div>
                  <Toggle
                    enabled={configuration.ordering}
                    onChange={(enabled) =>
                      handleConfigChange("ordering", enabled)
                    }

                  />
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Preview & Information */}
        <div className="space-y-6">
          {/* Configuration Preview */}
          <motion.div variants={itemVariants}>
            <Card className="sm:p-4 sticky top-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                  <FiInfo className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t("agent.configurationPreview")}
                </h3>
              </div>

              <div className="space-y-4">
                {/* Status */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {t("agent.status")}
                    </h4>
                    <Badge
                      variant={configuration.is_active ? "success" : "gray"}
                      className="flex items-center gap-1"
                    >
                      <div
                        className={`w-2 h-2 rounded-full ${configuration.is_active
                          ? "bg-green-500"
                          : "bg-gray-400"
                          } animate-pulse`}
                      />
                      {configuration.is_active
                        ? t("agent.active")
                        : t("agent.inactive")}
                    </Badge>
                  </div>
                </div>

                {configuration.is_active && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                      {t("agent.currentSettings")}
                    </h4>

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t("agent.tone")}:
                        </span>
                        <Badge variant="primary" size="sm">
                          {t(
                            TONE_OPTIONS.find(
                              (opt) => opt.value === configuration.tone
                            )?.labelKey || configuration.tone
                          )}
                        </Badge>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t("agent.language")}:
                        </span>
                        <Badge variant="secondary" size="sm">
                          {t(
                            LANGUAGE_OPTIONS.find(
                              (opt) => opt.value === configuration.language
                            )?.labelKey || configuration.language
                          )}
                        </Badge>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t("agent.identity")}:
                        </span>
                        <Badge variant="success" size="sm">
                          {t(
                            IDENTITY_OPTIONS.find(
                              (opt) => opt.value === configuration.identity
                            )?.labelKey || configuration.identity
                          )}
                        </Badge>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">
                          {t("agent.strategy")}:
                        </span>
                        <Badge variant="warning" size="sm">
                          {t(
                            HELPING_STRATEGY_OPTIONS.find(
                              (opt) =>
                                opt.value === configuration.helping_strategy
                            )?.labelKey || configuration.helping_strategy
                          )}
                        </Badge>
                      </div>
                    </div>
                  </div>
                )}

                {configuration.is_active && (
                  <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg p-4 border border-primary/20">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                      <FiMessageCircle className="w-4 h-4 text-primary" />
                      {t("agent.sampleResponse")}
                    </h4>
                    <div className="text-sm text-gray-600 dark:text-gray-300 italic">
                      {t("agent.sampleResponseContent", {
                        tone: t(
                          TONE_OPTIONS.find(
                            (opt) => opt.value === configuration.tone
                          )?.labelKey || configuration.tone
                        ),
                        identity: t(
                          IDENTITY_OPTIONS.find(
                            (opt) => opt.value === configuration.identity
                          )?.labelKey || configuration.identity
                        ),
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </motion.div>

          {/* Tips */}
          <motion.div variants={itemVariants}>
            <Card className="sm:p-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FiTarget className="text-amber-500" />
                {t("agent.tips")}
              </h3>

              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {t("agent.tip3Title")}
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      {t("agent.tip3Description")}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default AgentConfigurationPage;
